import { useEffect, useMemo, useRef } from 'react';
import { PiDotDuotone } from 'react-icons/pi';
import { clamp } from 'three/src/math/MathUtils.js';

import { CommanderActionTypesEnum, RotateAction } from '@spazious/dollhouse-commander';
import { FNode, SchemaFieldUnitTypesEnum } from '@spazious/dollhouse-fsetup';
import { useGraph } from '@spazious/dollhouse-store';
import { Flex, FlexProps, InputNumber, Slider, Typography } from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

import {
  useCommander,
  useNodesPlacementValidator,
  useSelectedNodes,
  useUnitConverter,
} from '../../hooks/index.js';
import { useDraggableLabel } from '../DraggableLabel/index.js';

import { Grid } from './Grid.js';

export interface RotationFieldProps extends FlexProps {
  icon?: React.ReactNode;
  format?: (value: number) => string;
  parse?: (value: string) => number;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
  defaultValue?: number;
  range?: boolean;
  title?: string;
  includeDragIcon?: boolean;
  selectOriginator?: boolean;
  unitType?: string;
  color?: string | undefined;
  group?: boolean;
}

export const RotationField: React.FC<RotationFieldProps> = ({
  icon,
  step = 0.01,
  format = (value) => value.toFixed((step.toString().split('.')[1] ?? '').length),
  parse = (value) => parseFloat(value),
  min,
  max,
  disabled,
  defaultValue = 0,
  range = true,
  title,
  includeDragIcon,
  selectOriginator = false,
  unitType,
  // eslint-disable-next-line no-unused-vars
  color: _,
  group = true,
  ...restProps
}) => {
  const actionRef = useRef</*CommanderAction*/ RotateAction | null>(null);
  const startValue = useRef(0);

  const commander = useCommander();
  const { displayUnit, standardUnit } = useUnitConverter();
  const targetNodes = useSelectedNodes(selectOriginator);

  useGraph();

  const { validator } = useNodesPlacementValidator(targetNodes);

  const isunitConversionApplicable = unitType === SchemaFieldUnitTypesEnum.Distance;
  const conversion = (formattedValue: number | string) => {
    if (isunitConversionApplicable) {
      return displayUnit(formattedValue);
    } else {
      return formattedValue;
    }
  };

  const [actualMin, actualMax, actualDefault] = useMemo(() => {
    return [parse(`${min ?? 0}`), parse(`${max ?? defaultValue}`), parse(`${defaultValue}`)];
  }, [parse, min, max, defaultValue]);

  const initialAngle = (nodes: FNode[]) => {
    return nodes.length > 1 ? 0 : nodes[0]?.rotation ?? 0;
  };

  const nodeValue = actionRef.current?.payload?.angle ?? initialAngle(targetNodes);
  const actualValue = clamp(nodeValue ?? actualDefault, actualMin, actualMax);
  const formattedValue = useMemo(() => format(actualValue), [format, actualValue]);

  useEffect(() => {
    // in case the field is being unmounted before committing changes, abort the changes
    return () => {
      if (actionRef.current) {
        actionRef.current.abort();
        actionRef.current = null;
      }
    };
  }, []);

  const handler = useEventCallback(
    ({
      first,
      last,
      // type,
      value,
    }: {
      first: boolean;
      last: boolean;
      type: string;
      value: number;
    }) => {
      if (first) {
        if (actionRef.current) {
          actionRef.current.abort();
        }
        const angle = initialAngle(targetNodes);
        actionRef.current = commander.createAction(
          CommanderActionTypesEnum.Rotate,
          targetNodes.map(({ selector }) => selector).join(';'),
          {
            group,
            angle,
          }
        );
        startValue.current = angle;
      } else if (last) {
        const valid = validator();
        if (valid) {
          actionRef.current?.commit();
        } else {
          actionRef.current?.abort();
        }
        actionRef.current = null;
      } else {
        actionRef.current?.update({
          angle: value,
          group,
        });
      }
    }
  );

  const dragProps = useDraggableLabel({
    value: actualValue,
    defaultValue,
    step: step,
    min: min,
    max: max,
    disabled,
    onDrag: handler,
  });

  const handleInputFocus = useEventCallback((evt: React.FocusEvent<HTMLInputElement>) => {
    handler({
      first: true,
      last: false,
      type: 'range',
      value: parse(standardUnit(evt.target.value)),
    });
  });

  const handleInputChange = useEventCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    handler({
      first: false,
      last: false,
      type: 'range',
      value: parse(standardUnit(evt.target.value)),
    });
  });

  const handleInputBlur = useEventCallback((evt: React.FocusEvent<HTMLInputElement>) => {
    handler({
      first: false,
      last: true,
      type: 'range',
      value: parse(standardUnit(evt.target.value)),
    });
  });

  const handleRangeStart = useEventCallback(
    (evt: React.KeyboardEvent<HTMLInputElement> | React.PointerEvent<HTMLInputElement>) => {
      evt.stopPropagation();
      handler({
        first: true,
        last: false,
        type: 'range',
        value: parse((evt.target as HTMLInputElement).value),
      });
    }
  );

  const handleRangeChange = useEventCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    handler({
      first: false,
      last: false,
      type: 'range',
      value: parse((evt.target as HTMLInputElement).value),
    });
  });

  const handleRangeEnd = useEventCallback(
    (evt: React.KeyboardEvent<HTMLInputElement> | React.PointerEvent<HTMLInputElement>) => {
      evt.stopPropagation();
      handler({
        first: false,
        last: true,
        type: 'range',
        value: parse((evt.target as HTMLInputElement).value),
      });
    }
  );

  return (
    <Grid {...restProps}>
      {icon && (
        <Typography
          size="large"
          style={{
            padding: 8,
            color: 'gray',
            userSelect: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={title}
          variant="label"
        >
          {icon}
        </Typography>
      )}
      <Flex alignItems="center" gap="var(--spacing-gap)" justifyContent="space-between">
        {range && Number.isFinite(min) && Number.isFinite(max) && (
          <Slider
            disabled={disabled}
            max={max}
            min={min}
            step={step}
            value={formattedValue}
            onChange={handleRangeChange}
            onKeyDown={handleRangeStart}
            onKeyUp={handleRangeEnd}
            onPointerDown={handleRangeStart}
            onPointerUp={handleRangeEnd}
            {...restProps}
          />
        )}
        <Flex>
          {includeDragIcon && (
            <PiDotDuotone
              {...dragProps}
              size="40px"
              style={{ ...dragProps.style, marginRight: '-35px', zIndex: 1 }}
              title="Drag up/down to change value, double click to reset"
            />
          )}
          <InputNumber
            disabled={disabled}
            format={conversion}
            max={max}
            min={min}
            step={step}
            style={{ width: '50px', paddingLeft: includeDragIcon ? '30px' : '7px' }}
            title={title}
            value={formattedValue}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            {...restProps}
          />
        </Flex>
      </Flex>
    </Grid>
  );
};
